import React, { useState } from 'react';
import { Button, Flex, Modal, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Dayjs } from 'dayjs';
import { useReattempt, useScheduleReattempt } from '@/hooksApi/useInventory';
import { formatDateTimeInIraqTimezone } from '@/utils/helpers/dateHelper';
import { reattemptDays } from './reattemptDays';
import useUserStore from '@/stores/userStore';

const DATE_FORMAT = 'YYYY-MM-DD HH:mm';
const dayLabel = (day: Dayjs) => day.format('dddd D MMMM');

interface ScheduleReattemptButtonProps {
  uid: string;
  /** The order's status history; its first out-for-delivery sets the earliest selectable day. */
  statuses?: Array<{ slug: string; pivot?: { created_at: string } }>;
  /** Renders a plain <button> with these classes (order-details header) instead of an antd small Button. */
  triggerClassName?: string;
}

const ScheduleReattemptButton: React.FC<ScheduleReattemptButtonProps> = ({
  uid,
  statuses,
  triggerClassName,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<Dayjs | null>(null);
  const days = open ? reattemptDays(statuses) : [];

  // Off for merchants without "Resend and Delayed RTOs" — the backend refuses them anyway.
  // Missing setting counts as on, same as the Inventory tab.
  const enabled = useUserStore(
    (state) => state.user?.setting?.is_delay_rto_and_resend_enabled ?? true,
  );
  const { data } = useReattempt(uid, enabled);
  const { mutate: schedule, isLoading } = useScheduleReattempt();
  const pending = data?.object?.status === 'pending' ? data.object : null;

  const close = () => {
    setValue(null);
    setOpen(false);
  };

  // The picked wall-clock is sent as-is: the backend reads it as Baghdad time.
  // Refusals (72h window, feature off, wrong status) are toasted by useSentryMutation.
  const handleOk = () => {
    if (!value) return;
    schedule(
      { uid, reattemptAt: value.format(DATE_FORMAT) },
      {
        onSuccess: () => {
          message.success(`${String(t('reattempt_scheduled'))}: ${dayLabel(value)}`);
          queryClient.invalidateQueries(['inventory-reattempt', uid]);
          close();
        },
      },
    );
  };

  if (!enabled) return null;

  const label = String(t('schedule_reattempt'));

  return (
    <>
      <Flex align='center' gap={8}>
        {triggerClassName ? (
          <button type='button' className={triggerClassName} onClick={() => setOpen(true)}>
            {label}
          </button>
        ) : (
          <Button size='small' onClick={() => setOpen(true)} className='text-xs'>
            {label}
          </Button>
        )}
        {pending ? (
          <span className='text-xs text-gray-500 whitespace-nowrap'>
            {`${String(t('reattempt_scheduled'))}: ${formatDateTimeInIraqTimezone(pending.reattempt_at)}`}
          </span>
        ) : null}
      </Flex>

      <Modal
        className='sd-font'
        title={label}
        open={open}
        onOk={handleOk}
        onCancel={close}
        okText={String(t('confirm'))}
        cancelText={String(t('cancel'))}
        okButtonProps={{ loading: isLoading, disabled: !value }}
        width={600}
        centered
        destroyOnClose
      >
        <Flex vertical gap={12} className='py-2'>
          <span className='text-sm font-medium'>{String(t('reattempt_pick_day'))}</span>
          <div
            role='radiogroup'
            aria-label={String(t('reattempt_pick_day'))}
            className='grid grid-cols-7 gap-2'
          >
            {days.map((day, i) => {
              const active = value?.isSame(day);
              return (
                <button
                  key={day.valueOf()}
                  type='button'
                  role='radio'
                  aria-checked={active}
                  aria-label={dayLabel(day)}
                  onClick={() => setValue(day)}
                  className={[
                    'flex flex-col items-center rounded-lg text-center transition-all active:scale-[0.98]',
                    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-[rgb(237,224,74)]',
                    active ? '' : 'bg-white hover:bg-[rgb(245,245,243)]',
                  ].join(' ')}
                  // Same selected/unselected vocabulary as the pickup PreferredTimePicker:
                  // border width carries selection, padding compensates so nothing shifts.
                  style={
                    active
                      ? {
                          border: '2px solid rgb(237,224,74)',
                          background: 'rgba(237,224,74,0.12)',
                          padding: '5px 2px',
                        }
                      : { border: '1px solid rgb(232,230,227)', padding: '6px 3px' }
                  }
                >
                  <span className='text-[11px] text-gray-500 leading-tight'>
                    {day.format('dddd')}
                  </span>
                  <span className='text-lg font-bold leading-tight'>{day.format('D')}</span>
                  <span className='text-[11px] text-gray-500 leading-tight'>
                    {day.format('MMMM')}
                  </span>
                  {i === 0 ? (
                    <span className='text-[10px] font-semibold text-[rgb(150,135,0)] leading-tight'>
                      {String(t('reattempt_earliest'))}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
          <p className='text-xs text-gray-600 m-0 rounded-md bg-[rgb(245,245,243)] px-3 py-2'>
            {value
              ? String(
                  t('reattempt_summary', {
                    resendDay: dayLabel(value.subtract(1, 'day')),
                    deliveryDay: dayLabel(value),
                  }),
                )
              : String(t('reattempt_hint'))}
          </p>
        </Flex>
      </Modal>
    </>
  );
};

export default ScheduleReattemptButton;
